import { Box, Card, CircularProgress, Grid, Icon, IconButton, InputLabel, Skeleton, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { Form, Formik, useFormikContext } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from 'react';
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import * as Yup from 'yup';

const TeamsCreateEdit = ({ id, closeCreateEdit }) => {

    const {
        nome,
        email,
        pessoas } = configForm.formField

    const [isLoadingInfo, setIsLoadingInfo] = useState(false);

    const [usuarios, setUsuarios] = useState([]);
    const [usuariosCoordenadores, setUsuariosCoordenadores] = useState([]);
    const [usuariosSupervisores, setUsuariosSupervisores] = useState([]);
    const [usuariosBackoffice, setUsuariosBackoffice] = useState([]);
    const [carteirasList, setCarteirasList] = useState([]);
    const [isLoadingUsuarios, setIsLoadingUsuarios] = useState(false);

    useEffect(() => {
        const fetchUsuarios = async () => {
            setIsLoadingUsuarios(true);
            try {
                const resCoord = await get('/users/list-gestores-coordenadores');
                setUsuariosCoordenadores(resCoord.list || []);
                
                const resSuper = await get('/users/list-supervisores');
                setUsuariosSupervisores(resSuper.list || []);

                const resBack = await get('/users/list-backoffice');
                setUsuariosBackoffice(resBack.list || []);

                const resMembros = await get('/users/list-membros-operacao');
                setUsuarios(resMembros.list || []);
                
                const resCart = await get('/carteira/list-all?todas=true');
                setCarteirasList(resCart.list || []);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
                setUsuariosCoordenadores([]);
                setUsuariosSupervisores([]);
                setUsuariosBackoffice([]);
                setUsuarios([]);
            } finally {
                setIsLoadingUsuarios(false);
            }
        };

        fetchUsuarios();
    }, []);

    const handleSubmit = (values, actions) => {
        const body = {
            id: values.id,
            nome: values.nome,
            email: values.email,
            emailRecortes: values.emailRecortes,
            emailEdital: values.emailEdital,
            emailPush: values.emailPush,
            emailEproc: values.emailEproc,
            ativo: values.ativo,
            usuarios: values.pessoas,
            usuariosCoordenadores: values.usuariosCoordenadores,
            usuariosSupervisores: values.usuariosSupervisores,
            usuariosBackoffice: values.usuariosBackoffice,
            carteiras: values.carteiras,
            isJuridico: values.isJuridico
        };

        postNoResponse("/equipe/save", body)
            .then(() => {
                successAlert(!id ? "Equipe criada com sucesso!" : "Alterações salvas!", handleSucess());
            })
            .catch(() => {
                errorAlert(!id ? "Erro ao criar equipe!" : 'Erro ao salvar alterações.');
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    };

    function handleSucess() {
        closeCreateEdit()
    }

    return (
        <Card id="user-info">
            <Box display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1, paddingTop: 0.75 }}>
                    <MDTypography variant="h5">
                        <Icon>arrow_back</Icon>
                    </MDTypography>
                </IconButton>
                <MDTypography variant="h6">{id ? "Editar" : "Adicionar"} Equipes</MDTypography>
            </Box>
            <Formik
                initialValues={{
                    id: null,
                    nome: '',
                    email: '',
                    emailRecortes: '',
                    emailEdital: '',
                    emailPush: '',
                    emailEproc: '',
                    pessoas: [],
                    usuariosCoordenadores: [],
                    usuariosSupervisores: [],
                    usuariosBackoffice: [],
                    carteiras: [],
                    isJuridico: false,
                    ativo: true,
                }}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(true);
                    handleSubmit(values, actions);
                }}>
                {({ values, errors, touched, isSubmitting, setFieldValue }) => {
                    return (
                        <Form id={configForm.formId} autoComplete="off">
                            <FetchAndSetData
                                id={id}
                                setIsLoadingInfo={setIsLoadingInfo}
                                closeCreateEdit={closeCreateEdit} />

                            <MDBox px={3}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={4}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={nome.type}
                                            label={nome.label}
                                            name={nome.name}
                                            value={values.nome}
                                            placeholder={nome.placeholder}
                                            error={errors.nome?.length > 0 && touched.nome}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={4}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={email.type}
                                            label={email.label}
                                            name={email.name}
                                            value={values.email}
                                            placeholder={email.placeholder}
                                            error={errors.email?.length > 0 && touched.email}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Carteiras</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo || isLoadingUsuarios}
                                            isMulti={true}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.nome}
                                            name='carteiras'
                                            placeholder="Selecione as Carteiras"
                                            value={values.carteiras}
                                            options={carteirasList}
                                            onChange={selectedOptions => {
                                                setFieldValue('carteiras', selectedOptions);
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={configForm.formField.emailRecortes.type}
                                            label={configForm.formField.emailRecortes.label}
                                            name={configForm.formField.emailRecortes.name}
                                            value={values.emailRecortes}
                                            placeholder={configForm.formField.emailRecortes.placeholder}
                                        />
                                        <MDTypography variant="caption" color="text" sx={{ ml: 1 }}>
                                            Se vazio, usa o e-mail geral da equipe
                                        </MDTypography>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={configForm.formField.emailEdital.type}
                                            label={configForm.formField.emailEdital.label}
                                            name={configForm.formField.emailEdital.name}
                                            value={values.emailEdital}
                                            placeholder={configForm.formField.emailEdital.placeholder}
                                        />
                                        <MDTypography variant="caption" color="text" sx={{ ml: 1 }}>
                                            Se vazio, usa o e-mail geral da equipe
                                        </MDTypography>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={configForm.formField.emailPush.type}
                                            label={configForm.formField.emailPush.label}
                                            name={configForm.formField.emailPush.name}
                                            value={values.emailPush}
                                            placeholder={configForm.formField.emailPush.placeholder}
                                        />
                                        <MDTypography variant="caption" color="text" sx={{ ml: 1 }}>
                                            Se vazio, usa o e-mail geral da equipe
                                        </MDTypography>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={configForm.formField.emailEproc.type}
                                            label={configForm.formField.emailEproc.label}
                                            name={configForm.formField.emailEproc.name}
                                            value={values.emailEproc}
                                            placeholder={configForm.formField.emailEproc.placeholder}
                                        />
                                        <MDTypography variant="caption" color="text" sx={{ ml: 1 }}>
                                            Se vazio, usa o e-mail geral da equipe
                                        </MDTypography>
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Coordenadores</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo || isLoadingUsuarios}
                                            isMulti={true}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.nome}
                                            name='usuariosCoordenadores'
                                            placeholder="Selecione os Coordenadores"
                                            value={values.usuariosCoordenadores}
                                            options={usuariosCoordenadores}
                                            onChange={selectedOptions => {
                                                setFieldValue('usuariosCoordenadores', selectedOptions);
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Supervisores</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo || isLoadingUsuarios}
                                            isMulti={true}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.nome}
                                            name='usuariosSupervisores'
                                            placeholder="Selecione os Supervisores"
                                            value={values.usuariosSupervisores}
                                            options={usuariosSupervisores.length > 0 ? usuariosSupervisores : usuariosCoordenadores}
                                            onChange={selectedOptions => {
                                                setFieldValue('usuariosSupervisores', selectedOptions);
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>BackOffice</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo || isLoadingUsuarios}
                                            isMulti={true}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.nome}
                                            name='usuariosBackoffice'
                                            placeholder="Selecione o BackOffice"
                                            value={values.usuariosBackoffice}
                                            options={usuariosBackoffice.length > 0 ? usuariosBackoffice : usuarios}
                                            onChange={selectedOptions => {
                                                setFieldValue('usuariosBackoffice', selectedOptions);
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Membros / Operadores</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo || isLoadingUsuarios}
                                            isMulti={true}
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.nome}
                                            name={pessoas.name}
                                            placeholder={pessoas.placeholder}
                                            value={values.pessoas}
                                            options={usuarios}
                                            onChange={selectedOptions => {
                                                setFieldValue('pessoas', selectedOptions);
                                            }}
                                        />
                                        {errors.pessoas && touched.pessoas && (
                                            <MDTypography variant="caption" color="error">
                                                {errors.pessoas}
                                            </MDTypography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={3} style={{ display: 'flex', alignItems: 'center', marginTop: '1.2rem' }}>
                                        {isLoadingInfo ? <Skeleton width={"80%"} sx={{ mt: 2 }} /> :
                                            <Box display="flex" alignItems="center" lineHeight={1}>
                                                <MDTypography variant="button">
                                                    Ativo
                                                </MDTypography>
                                                <Box ml={1} mr={3}>
                                                    <Switch checked={values.ativo || false}
                                                        onChange={(event) => setFieldValue('ativo', event.target.checked)} />
                                                </Box>
                                                <MDTypography variant="button">
                                                    Atuação Jurídica?
                                                </MDTypography>
                                                <Box ml={1}>
                                                    <Switch checked={values.isJuridico || false}
                                                        onChange={(event) => setFieldValue('isJuridico', event.target.checked)} />
                                                </Box>
                                            </Box>
                                        }
                                    </Grid>
                                </Grid>
                            </MDBox>
                            <Box display="flex" alignItems="center" justifyContent="end" p={2}>
                                <MDButton
                                    disabled={isLoadingInfo}
                                    size="large"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    type="submit"
                                    endIcon={isSubmitting ?
                                        <CircularProgress
                                            color="inherit"
                                            size={20} sx={{ ml: 0, mr: 0 }} /> : null}>
                                    {!isSubmitting && (id ? "Salvar" : "Adicionar")}
                                </MDButton>
                            </Box>
                        </Form>)
                }}
            </Formik>
        </Card>
    );
};

TeamsCreateEdit.propTypes = {
    id: PropTypes.string,
    closeCreateEdit: PropTypes.func.isRequired,
};

export default memo(TeamsCreateEdit);

const FetchAndSetData = ({ id, setIsLoadingInfo, closeCreateEdit }) => {
    const { setFieldValue } = useFormikContext();

    useEffect(() => {
        if (id) {
            setIsLoadingInfo(true);
            get(`/equipe/by-id/${id}`).then(data => {
                const { id, nome, email, emailRecortes, emailEdital, emailPush, emailEproc, usuarios, ativo, isJuridico, usuariosCoordenadores, usuariosSupervisores, usuariosBackoffice, carteiras } = data;
                setFieldValue('pessoas', usuarios || []);
                setFieldValue('usuariosCoordenadores', usuariosCoordenadores || []);
                setFieldValue('usuariosSupervisores', usuariosSupervisores || []);
                setFieldValue('usuariosBackoffice', usuariosBackoffice || []);
                setFieldValue('carteiras', carteiras || []);
                setFieldValue('nome', nome);
                setFieldValue('email', email || '');
                setFieldValue('emailRecortes', emailRecortes || '');
                setFieldValue('emailEdital', emailEdital || '');
                setFieldValue('emailPush', emailPush || '');
                setFieldValue('emailEproc', emailEproc || '');
                setFieldValue('isJuridico', isJuridico);
                setFieldValue('ativo', ativo);
                setFieldValue('id', id);
                setIsLoadingInfo(false);
            }).catch((erro) => errorAlert("Equipe não encontrada!", closeCreateEdit));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return null;
};

export const configForm = {
    formId: "configuration-form",
    formField: {
        nome: {
            name: "nome",
            label: "Nome",
            placeholder: "Informe o nome...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
        email: {
            name: "email",
            label: "E-mail Geral da Equipe",
            placeholder: "Ex: civel01@raadvogados.adv.br",
            type: "text"
        },
        emailRecortes: {
            name: "emailRecortes",
            label: "E-mail Robô Recortes (AASP)",
            placeholder: "Ex: recortes.civel@raadvogados.adv.br",
            type: "text"
        },
        emailEdital: {
            name: "emailEdital",
            label: "E-mail Robô Edital",
            placeholder: "Ex: edital.civel@raadvogados.adv.br",
            type: "text"
        },
        emailPush: {
            name: "emailPush",
            label: "E-mail Robô Push / Intimação",
            placeholder: "Ex: push.civel@raadvogados.adv.br",
            type: "text"
        },
        emailEproc: {
            name: "emailEproc",
            label: "E-mail Robô EPROC / T-Legal",
            placeholder: "Ex: eproc.civel@raadvogados.adv.br",
            type: "text"
        },
        pessoas: {
            name: "pessoas",
            label: "Pessoas",
            placeholder: "Selecione as pessoas",
            errorMsg: "Campo Obrigatório.",
        },
        usuariosGestores: {
            name: "usuariosGestores",
            label: "Usuários Gestores",
            placeholder: "Selecione os usuários",
            errorMsg: "Campo Obrigatório.",
        },
    }
}

const {
    formField: {
        nome,
        email,
        emailRecortes,
        emailEdital,
        emailPush,
        emailEproc,
        pessoas
    },
} = configForm;

export const validationSchema = Yup.object().shape({
    [nome.name]: Yup.string()
        .required(nome.errorMsg),
    [email.name]: Yup.string(),
    [emailRecortes.name]: Yup.string(),
    [emailEdital.name]: Yup.string(),
    [emailPush.name]: Yup.string(),
    [emailEproc.name]: Yup.string(),
    [pessoas.name]: Yup.array()
        .required(pessoas.errorMsg),
});
